import React from "react";
import { Avatar, Box, Typography, keyframes } from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAssistantChat } from "./AssistantChatContext";

/**
 * A small AI assistant button to be placed on product cards (top left).
 * Checks authentication before opening the chat popup.
 *
 * @param {object} product - The product object (must have at least url/product_url)
 */
const attentionPulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4), 0 0 0 0 rgba(255, 140, 56, 0.7);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.5), 0 0 0 6px rgba(255, 140, 56, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4), 0 0 0 0 rgba(255, 140, 56, 0);
  }
`;

const AssistantButton = ({ product, sx = {} }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { openChat } = useAssistantChat();

  const handleClick = (e) => {
    e.stopPropagation(); // Prevent card click from firing
    e.preventDefault();

    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    openChat(product);
  };

  return (
    <Box
      onClick={handleClick}
      aria-label="Ask AI about this product"
      sx={{
        position: "absolute",
        top: { xs: 8, sm: 10 },
        left: { xs: 8, sm: 10 },
        zIndex: 10,
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        px: { xs: 1, sm: 1.5 },
        py: { xs: 0.75, sm: 0.75 },
        borderRadius: "24px",
        background: "rgba(20, 20, 25, 0.65)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        border: "1px solid rgba(255, 140, 56, 0.5)",
        color: "#FFF",
        cursor: "pointer",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        animation: `${attentionPulse} 2s infinite`,
        "&:hover": {
          animation: "none",
          background: "rgba(20, 20, 25, 0.85)",
          border: "1px solid rgba(255, 140, 56, 0.8)",
          transform: "translateY(-2px) scale(1.02)",
          boxShadow: "0 8px 20px rgba(0,0,0,0.6), 0 0 12px rgba(255, 140, 56, 0.4)",
        },
        "&:active": {
          transform: "scale(0.95)",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          top: -12,
          left: -12,
          right: -12,
          bottom: -12,
        },
        ...sx,
      }}
    >
      <Avatar
        src={require("../../assets/images/login-handpik-logo_cropped.png")}
        sx={{
          width: { xs: 14, sm: 16 },
          height: { xs: 14, sm: 16 },
          bgcolor: "transparent",
          img: { objectFit: "contain", filter: "brightness(0) invert(1)" },
        }}
      />
      <Typography sx={{ fontFamily: "Poppins-SemiBold", fontSize: { xs: "10px", sm: "11px" }, letterSpacing: "0.5px", lineHeight: 1 }}>
        AI
      </Typography>
    </Box>
  );
};

export default AssistantButton;
